import { CAFY_MESSAGES } from '../constants/cafyMessages';

// Transforms the API response from GET /api/v1/domains/tree into the nested
// flow structure expected by GoalSettingFlow.
//
// API shape:  Domain[] → sub_domains[] → taxonomy_terms[]
// Output:     { initial_dialogue, flows }

// The line to call a sub-domain by when asking about it. It is the description
// the person just picked ("walking, freezing, shaking or keeping your balance"),
// not the internal category label ("Movement & Mobility") — hearing the label
// back reads as a non sequitur, the same reason the reply after the sub-domain
// step echoes the description too.
//
// The descriptions are written as mid-sentence fragments carrying their own full
// stop, which would double up inside the question.
const areaPhrase = (sub) =>
	(sub.goal_description || sub.name)
		.trim()
		.replace(/\.+$/, '')
		// One of them is written to follow "staying active": "like doing
		// exercises, walking, stretching". That lead-in is a stray word once the
		// fragment becomes the object of the question. Harmless once the copy is
		// reworded upstream — there would be no "like" left to drop.
		.replace(/^like\s+/i, '');

export const transformDomainTreeToFlow = (domainTree) => {
	const initialOptions = domainTree.map((domain) => ({
		id: `domain_${domain.id}`,
		text: domain.goal_description || domain.name,
		next_flow: `flow_${domain.id}`,
	}));

	const flows = {};

	domainTree.forEach((domain) => {
		const flowId = `flow_${domain.id}`;

		// Step 0: sub-domain selection
		const subDomainStep = {
			id: `${flowId}_main`,
			message: CAFY_MESSAGES.subDomainStep.message,
			message_after_selection: CAFY_MESSAGES.subDomainStep.messageAfterSelection,
			question_type: 'multiple_choice',
			allow_multiple: true,
			options: domain.sub_domains.map((sub) => ({
				id: `subdomain_${sub.id}`,
				name: sub.name,
				text: sub.goal_description || sub.name,
				next_step: `step_subdomain_${sub.id}`,
			})),
		};

		// Steps: one step per sub-domain, listing its taxonomy terms.
		const termSteps = domain.sub_domains.map((sub) => ({
			id: `step_subdomain_${sub.id}`,
			message: CAFY_MESSAGES.termStep(areaPhrase(sub)),
			question_type: 'multiple_choice',
			allow_multiple: true,
			options: sub.taxonomy_terms.map((term) => ({
				id: `term_${term.id}`,
				name: term.name,
				text: term.goal_description || term.name,
				goal_setting: true,
				short_description: term.short_description || '',
				// The sentence CAFY replies once the goal is set (split from goal_description Aug 17)
				goal_description: term.goal_setting_message || '',
				smart_watch: false,
			})),
		}));

		flows[flowId] = {
			id: flowId,
			title: domain.name,
			steps: [subDomainStep, ...termSteps],
		};
	});

	return {
		initial_dialogue: {
			title: CAFY_MESSAGES.initial.title,
			message: CAFY_MESSAGES.initial.message,
			question_type: 'multiple_choice',
			allow_multiple: true,
			options: initialOptions,
		},
		flows,
	};
};

// Builds a flat termId → term lookup map for App.js (getGoalDescription, getSmartWatchValue)
export const buildTermsMap = (domainTree) => {
	const map = {};
	domainTree.forEach((domain) => {
		domain.sub_domains.forEach((sub) => {
			sub.taxonomy_terms.forEach((term) => {
				map[`term_${term.id}`] = {
					...term,
					smart_watch: false,
				};
			});
		});
	});
	return map;
};
